export type StoragePressure = 'unknown' | 'normal' | 'warning' | 'critical'

export interface LocalStorageStatus {
  usage?: number
  quota?: number
  ratio?: number
  persisted?: boolean
  persistenceSupported?: boolean
  pressure: StoragePressure
}

export type PersistenceRequestResult = 'granted' | 'already-granted' | 'denied' | 'unsupported'

interface PersistenceStorageManager {
  persist?: () => Promise<boolean>
  persisted?: () => Promise<boolean>
}

export const storagePressure = (usage?: number, quota?: number): StoragePressure => {
  if (usage === undefined || quota === undefined || quota <= 0) return 'unknown'
  const ratio = usage / quota
  if (ratio >= 0.9) return 'critical'
  if (ratio >= 0.7) return 'warning'
  return 'normal'
}

export const formatBytes = (bytes?: number) => {
  if (bytes === undefined || !Number.isFinite(bytes)) return 'Unknown'
  if (bytes < 1024) return `${Math.max(0, Math.round(bytes))} B`
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

export const readLocalStorageStatus = async (): Promise<LocalStorageStatus> => {
  const estimate = await navigator.storage?.estimate?.()
  const persisted = await navigator.storage?.persisted?.()
  const usage = estimate?.usage
  const quota = estimate?.quota
  return {
    usage,
    quota,
    ratio: usage !== undefined && quota ? usage / quota : undefined,
    persisted,
    persistenceSupported: typeof navigator.storage?.persist === 'function' && typeof navigator.storage?.persisted === 'function',
    pressure: storagePressure(usage, quota),
  }
}

export const requestPersistentStorage = async (
  storage: PersistenceStorageManager | undefined = typeof navigator === 'undefined' ? undefined : navigator.storage,
): Promise<PersistenceRequestResult> => {
  if (!storage?.persist || !storage.persisted) return 'unsupported'
  // Invoke persist() before the first await so browsers that associate this
  // request with the button's user activation do not lose that gesture.
  if (await storage.persist()) return 'granted'
  return await storage.persisted() ? 'already-granted' : 'denied'
}
